import React, { Suspense, useEffect, useState, useCallback } from 'react';
import { useRouteLoaderData, Await } from 'react-router-dom';
import {
  AnnualReportBanner,
  DataSetsFilterAccordion,
  MessageBox,
  PageNavigation,
  PdfEmbed,
  PreviewWordControl,
  RelevantLinks,
  SideNavigation,
  Tag,
  PdfSideNavigation,
} from 'Components';
import {
  CONTENT_ITEM_TYPES,
  EMISSIONS_DATA_MESSAGE,
  ERROR_PAGE,
  IS_PREVIEW_MODE,
  IS_PUBLISHED_APP,
  MESSAGE_BOX_TYPE,
  PAGE_NOT_FOUND_PAGE,
  PATHS,
  TAG_STYLE_ENUMS,
} from 'Constants';
import DefaultCoverImage from './DefaultCoverImage';
import style from './AnnualReportCover.module.scss';
import { AnnualReportCoverLoading } from 'Pages';
import { ERROR_CONTENT, PAGE_NOT_FOUND } from 'Constants/StaticContents';
import { STATUS_TYPES } from 'Utils/Error';
import AnnualReportSearch from 'Components/AnnualReportSearch/AnnualReportSearch';
import HTMLReactParser from 'html-react-parser';

/**
 * AnnualReportCover.jsx
 *
 * @summary This component is page view for annual report cover page.
 */
export default function AnnualReportCover() {
  const { base } = useRouteLoaderData(PATHS.ANNUAL_REPORT.ID);
  const [coverImageError, setCoverImageError] = useState(false);
  const [printReady, setPrintReady] = useState(false);
  const [pdfSideMenuData, setPdfSideMenuData] = useState([]);
  const [tab, setTab] = useState(0);

  //Search in annual report states
  const [searchData, setSearchData] = useState({});

  const handleImageLoadError = () => {
    setCoverImageError(true);
  };

  return (
    // use Suspense to show loading page before `annualReport` finishes loading
    <Suspense fallback={<AnnualReportCoverLoading />}>
      <Await resolve={base} errorElement={ERROR_PAGE()}>
        {(loadedAnnualReport) => {
          // if AR was not found
          if (loadedAnnualReport.isError) {
            if (loadedAnnualReport.statusCode === STATUS_TYPES.NOT_FOUND) return PAGE_NOT_FOUND_PAGE(ERROR_CONTENT.NOT_FOUND);
            if (loadedAnnualReport.statusCode === STATUS_TYPES.RANGE_ERROR) return ERROR_PAGE(PAGE_NOT_FOUND(ERROR_CONTENT.INVALID_CONTENT));
            if (loadedAnnualReport.statusCode === STATUS_TYPES.OTHER) return ERROR_PAGE();
            return ERROR_PAGE();
          }
          const {
            banner,
            coverImageSrc,
            department,
            isConsolidatedData,
            logo,
            navigationInfo,
            portfolio,
            relevantLinks,
            reportingPeriod,
            routes,
            routeTitle,
            system,
            primaryPDF,
            dataTables,
          } = loadedAnnualReport;

          useEffect(() => {
            if (loadedAnnualReport && system?.type === 'climate_disclosure_report' && dataTables) {
              const dataTablesArray = Array.isArray(dataTables) ? dataTables : [dataTables];

              if (dataTablesArray.length > 0) {
                setPdfSideMenuData([
                  {
                    title: String(routeTitle),
                    items: [String(primaryPDF?.name)],
                  },
                  {
                    title: 'Climate disclosure data tables',
                    items: dataTablesArray.map((table) => table.title),
                  },
                ]);
              } else {
                setPdfSideMenuData([
                  {
                    title: String(routeTitle),
                    items: [String(primaryPDF?.name)],
                  },
                ]);
              }
            }
          }, []);

          const handleItemClick = useCallback((flatIndex) => {
            setTab(flatIndex);
          }, []);

          // Get page publications navigation link data. Publication data is only filtered to those within its collection (done in the loader.js)
          const navigationData = navigationInfo || {};

          // Map content type to tag display values
          const getTagInfo = (contentType) => {
            const tagMap = {
              [CONTENT_ITEM_TYPES.ANNUAL_REPORT.ID]: CONTENT_ITEM_TYPES.ANNUAL_REPORT,
              [CONTENT_ITEM_TYPES.CLIMATE_DISCLOSURE_REPORT.ID]: CONTENT_ITEM_TYPES.CLIMATE_DISCLOSURE_REPORT,
            };
            return tagMap[contentType] || { TAG: 'AR', TAG_FULL_TEXT: 'annual report' };
          };

          const tagInfo = getTagInfo(system?.type);
          const tagText = tagInfo.TAG;
          const tagFullText = tagInfo.TAG_FULL_TEXT;

          document.title = routeTitle;

          // cover image will show default cover image if no image is found
          const coverImage = () => {
            return (
              <div className={style.coverImage}>
                {coverImageSrc && !coverImageError ? (
                  <img className={style.coverImage} src={coverImageSrc} alt={`${routeTitle} cover image`} onLoad={() => setPrintReady(true)} onError={handleImageLoadError} />
                ) : (
                  <DefaultCoverImage entityName={department.name} reportTitle={routeTitle} />
                )}
                {printReady && <span id="readyForPrint" hidden key={new Date().getTime()}></span>}
              </div>
            );
          };

          return (
            <>
              <AnnualReportBanner department={department} portfolio={portfolio} bannerImgSrc={banner} logoImgSrc={logo.url} logoImgAltText={logo.altText} />
              {system?.type === 'climate_disclosure_report' && (
                <>
                  <div>
                    <h1 id={style.reportTitle}>
                      <span>
                        {department?.name && <span id={style.departmentName}>{department.name}</span>}
                        {<Tag displayText={tagText} fullText={tagFullText} type={TAG_STYLE_ENUMS.HIGHLIGHT} />}
                        {reportingPeriod && <Tag displayText={reportingPeriod} type={TAG_STYLE_ENUMS.GREY} />}
                      </span>
                      {routeTitle || <span className="noContentOutlineInside"></span>}
                    </h1>
                    <div className={style.cdWrapper}>
                      <PdfSideNavigation data={pdfSideMenuData} onItemClick={handleItemClick} activeItem={tab} />

                      <div className={style.cdContentWrapper}>
                        {tab === 0 && <PdfEmbed pdfSrc={primaryPDF?.url} fileName={primaryPDF?.name} />}
                        {(dataTables && Array.isArray(dataTables) ? dataTables : [dataTables]).map((dataTable, index) => {
                          // check for array og objects or single object
                          const tabIndex = index + 1;

                          return (
                            tab === tabIndex && (
                              <div key={dataTable.title || `data-table-${index}`}>
                                <h4>{dataTable.title}</h4>
                                <div>
                                  {HTMLReactParser(dataTable?.data ?? '')}
                                  {dataTable?.caveatMsg !== '' && <span>{dataTable.caveatMsg}</span>}
                                  {isConsolidatedData && (
                                    <span>
                                      The <strong>{portfolio.name} portfolio</strong> reports consolidated emissions data on behalf of other entities and operations within the
                                      portfolio, including the <strong>{department.name}</strong>.
                                    </span>
                                  )}
                                  <MessageBox type={MESSAGE_BOX_TYPE.INFORMATION} style={{ paddingTop: '40px' }} alwaysExpanded={true}>
                                    <span tabIndex={0} className={[style.aboutThisData].join(' ')}>
                                      {EMISSIONS_DATA_MESSAGE}
                                    </span>
                                  </MessageBox>
                                </div>
                              </div>
                            )
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <PageNavigation navigationData={navigationData} />
                </>
              )}

              {system?.type === 'annual_report' && (
                <>
                  <h1 id={style.reportTitle}>
                    <span>
                      {department?.name && <span id={style.departmentName}>{department.name}</span>}
                      {<Tag displayText={tagText} fullText={tagFullText} type={TAG_STYLE_ENUMS.HIGHLIGHT} />}
                      {reportingPeriod && <Tag displayText={reportingPeriod} type={TAG_STYLE_ENUMS.GREY} />}
                    </span>
                    {routeTitle || <span className="noContentOutlineInside"></span>}
                  </h1>
                  <div id={style.annualReportCover}>
                    <div>
                      {IS_PREVIEW_MODE && (
                        <DataSetsFilterAccordion title="Export Word" isRequired={false}>
                          <PreviewWordControl annualReportCodename={system.codename} pdfFileName={routeTitle || null} />
                        </DataSetsFilterAccordion>
                      )}
                      <SideNavigation menus={routes} searchData={searchData} desktopLookInMobile />
                      <PageNavigation navigationData={navigationData} />
                    </div>
                    <div>
                      {IS_PUBLISHED_APP && (
                        <AnnualReportSearch annualReportGuid={system?.id} searchData={searchData} setSearchData={setSearchData} routes={routes} frontpage={true} />
                      )}
                      {coverImage()}
                    </div>
                  </div>
                </>
              )}
              <RelevantLinks linkList={relevantLinks} />
            </>
          );
        }}
      </Await>
    </Suspense>
  );
}
